/**
 * Yandex Metrika goals — the single list of every JS goal the app sends.
 *
 * Each identifier here must exist as a "JavaScript event" goal on counter
 * METRIKA_ID, otherwise Metrika silently drops it. The goals are created by
 * `scripts/metrika/setup-goals.ts`, which reads this same list, so adding a goal
 * means adding it here and re-running the script. Plan and funnels:
 * project_knowledge/ANALYTICS.md.
 *
 * Never put personal data (email, name, phone) into goal params or userParams.
 */

export const METRIKA_ID = 104713371;

declare global {
  interface Window {
    ym?: (id: number, action: string, ...args: unknown[]) => void;
  }
}

/** Goal id → human name shown in the Metrika UI. */
export const GOALS = {
  // Acquisition & activation
  signup_completed: 'Регистрация завершена',
  login_completed: 'Вход в аккаунт',
  project_created: 'Создан проект',
  generation_any: 'Любая AI-генерация',
  limit_reached: 'Упёрся в лимит тарифа',
  // Modules
  gen_market_analysis: 'Генерация: анализ рынка',
  gen_personas: 'Генерация: персоны',
  gen_positioning: 'Генерация: позиционирование',
  gen_business_model: 'Генерация: бизнес-модель',
  gen_marketing_strategy: 'Генерация: маркетинговая стратегия',
  gen_mvp_plan: 'Генерация: план MVP',
  gen_financial_model: 'Генерация: AI-рекомендации финмодели',
  gen_methodology: 'Генерация: методология',
  chat_research: 'Сообщение в чат-исследование',
  chat_mentor: 'Сообщение AI-ментору',
  builder_created: 'Билдер: создан сайт',
  builder_generate: 'Билдер: сайт сгенерирован',
  builder_chat: 'Билдер: правка через чат',
  builder_publish: 'Билдер: сайт опубликован',
  tools_search: 'Подбор AI-инструментов: поиск',
  tools_click: 'Подбор AI-инструментов: переход',
  feedback_submitted: 'Отправлен отзыв',
  // Monetization
  checkout_started: 'Начата оплата',
  payment_success: 'Оплата прошла',
  payment_fail: 'Оплата не прошла',
} as const;

export type Goal = keyof typeof GOALS;
export type GoalParams = Record<string, string | number | boolean>;

/** Fire a Metrika goal. No-op when the counter is not loaded; never throws. */
export function reachGoal(goal: Goal, params?: GoalParams): void {
  try {
    if (typeof window !== 'undefined' && typeof window.ym === 'function') {
      if (params) window.ym(METRIKA_ID, 'reachGoal', goal, params);
      else window.ym(METRIKA_ID, 'reachGoal', goal);
    }
  } catch {
    // Analytics must never break the app.
  }
}

/** Visitor-level params: lets reports be split by tariff. Pseudonymous id only. */
export function setVisitorParams(userId: string, params: GoalParams): void {
  try {
    if (typeof window !== 'undefined' && typeof window.ym === 'function') {
      window.ym(METRIKA_ID, 'setUserID', userId);
      window.ym(METRIKA_ID, 'userParams', { UserID: userId, ...params });
    }
  } catch {
    // Analytics must never break the app.
  }
}

const AI_POST_GOALS: Array<[RegExp, Goal]> = [
  [/^\/api\/ai\/market-analysis\/?$/, 'gen_market_analysis'],
  [/^\/api\/ai\/personas\/?$/, 'gen_personas'],
  [/^\/api\/ai\/positioning\/?$/, 'gen_positioning'],
  [/^\/api\/ai\/business-model\/?$/, 'gen_business_model'],
  [/^\/api\/ai\/marketing-strategy\/?$/, 'gen_marketing_strategy'],
  [/^\/api\/ai\/mvp-plans\/?$/, 'gen_mvp_plan'],
  [/^\/api\/ai\/financial-model\/?$/, 'gen_financial_model'],
  [/^\/api\/ai\/methodologies\/?$/, 'gen_methodology'],
  [/^\/api\/ai\/research-chat\/?$/, 'chat_research'],
  [/^\/api\/ai\/mentor-chat\/?$/, 'chat_mentor'],
  [/^\/api\/builder\/[^/]+\/generate$/, 'builder_generate'],
  [/^\/api\/builder\/[^/]+\/chat$/, 'builder_chat'],
];

/** Short module name for limit_reached params, e.g. "personas", "builder". */
function moduleOf(path: string): string {
  const m = path.match(/^\/api\/(?:ai\/)?([^/?]+)/);
  return m?.[1] ?? 'unknown';
}

export interface GoalHit {
  goal: Goal;
  params?: GoalParams;
}

/**
 * Maps a finished API request to the goals it represents. Pure: apiFetch calls it
 * after every response, so goals for all AI modules come from one place instead of
 * being sprinkled over pages.
 *
 * @param path   request path without origin, e.g. "/api/ai/personas"
 * @param status HTTP status of the response
 * @param body   parsed JSON request body, when there was one
 * @param errorCode the `error` field of a 403 response, when there was one
 */
export function goalsForApiResponse(
  method: string | undefined,
  path: string,
  status: number,
  body?: Record<string, unknown> | null,
  errorCode?: string,
): GoalHit[] {
  if ((method || 'GET').toUpperCase() !== 'POST') return [];
  const cleanPath = path.split('?')[0] ?? path;
  const ok = status >= 200 && status < 300;

  if (status === 403 && errorCode === 'Limit reached') {
    return [{ goal: 'limit_reached', params: { module: moduleOf(cleanPath) } }];
  }
  if (!ok) return [];

  if (cleanPath === '/api/projects') return [{ goal: 'project_created' }];
  if (cleanPath === '/api/builder') return [{ goal: 'builder_created' }];
  if (/^\/api\/builder\/[^/]+\/publish$/.test(cleanPath)) return [{ goal: 'builder_publish' }];
  if (cleanPath === '/api/robokassa/create-payment') {
    const params: GoalParams = {};
    if (typeof body?.subscriptionType === 'string') params.plan = body.subscriptionType;
    if (typeof body?.billingPeriod === 'string') params.period = body.billingPeriod;
    return [{ goal: 'checkout_started', params }];
  }

  for (const [re, goal] of AI_POST_GOALS) {
    if (re.test(cleanPath)) return [{ goal }, { goal: 'generation_any', params: { module: goal } }];
  }
  return [];
}
